import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { backend_url } from "../../App";
import { useAdminMode } from "../../context/AdminModeContext";
import "./OrderBell.css";

const POLL_MS = 30000;
const SEEN_KEY = "orderBellLastSeen"; // per browser: when this admin last cleared the bell

const readSeen = () => {
  try {
    const v = localStorage.getItem(SEEN_KEY);
    if (v) return v;
    const now = new Date().toISOString(); // first run: start from now, no flood of old orders
    localStorage.setItem(SEEN_KEY, now);
    return now;
  } catch {
    return new Date().toISOString();
  }
};

const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return new Date(d).toLocaleDateString();
};

// Short two-note chime (no audio file needed). Browsers only allow sound
// after the admin has clicked somewhere on the page at least once.
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[880, 0], [1320, 0.16]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.4);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch { /* sound is optional */ }
};

const OrderBell = () => {
  const navigate = useNavigate();
  const { setMode } = useAdminMode();
  const [orders, setOrders] = useState([]);
  const [count, setCount]   = useState(0);
  const [open, setOpen]     = useState(false);
  const [popup, setPopup]   = useState(null); // newest arrival, shown briefly
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const knownIds = useRef(null); // ids already shown; null until the first poll
  const wrapRef  = useRef(null);

  const poll = useCallback(async () => {
    const token = sessionStorage.getItem("adminToken");
    if (!token) return;
    try {
      const res = await fetch(
        `${backend_url}/api/admin/order-notifications?since=${encodeURIComponent(readSeen())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!data.success) return;

      // Alert only for orders that arrived since the previous poll
      if (knownIds.current) {
        const fresh = data.orders.filter((o) => !knownIds.current.has(o._id));
        if (fresh.length) {
          const o = fresh[0];
          const msg = `${o.name} — ₹${Number(o.totalPrice).toFixed(2)}${fresh.length > 1 ? ` (+${fresh.length - 1} more)` : ""}`;
          setPopup({ order: o, text: msg });
          playChime();
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            const n = new Notification("🛍️ New order received", {
              body: `${msg}\n${o.productId?.name || ""}`, tag: o._id,
            });
            n.onclick = () => { window.focus(); n.close(); };
          }
        }
      }
      knownIds.current = new Set(data.orders.map((o) => o._id));
      setOrders(data.orders);
      setCount(data.count);
    } catch { /* offline — try again next poll */ }
  }, []);

  useEffect(() => {
    poll();
    const timer = setInterval(poll, POLL_MS);
    const onVisible = () => document.visibilityState === "visible" && poll();
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [poll]);

  // hide the popup after a few seconds
  useEffect(() => {
    if (!popup) return;
    const t = setTimeout(() => setPopup(null), 7000);
    return () => clearTimeout(t);
  }, [popup]);

  // close the dropdown when clicking outside it
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const markAllRead = () => {
    try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setOrders([]);
    setCount(0);
  };

  const openOrder = (order) => {
    setOpen(false);
    setPopup(null);
    setMode("retail");
    navigate("/orders", { state: order ? { search: order._id } : undefined });
  };

  const enableDesktop = async () => {
    if (typeof Notification === "undefined") return;
    setPermission(await Notification.requestPermission());
  };

  return (
    <div className="ob-wrap" ref={wrapRef}>
      <button
        className={`ob-bell ${count > 0 ? "has-new" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={count > 0 ? `${count} new order${count > 1 ? "s" : ""}` : "No new orders"}
        aria-label="Order notifications"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
        {count > 0 && <span className="ob-badge">{count > 99 ? "99+" : count}</span>}
      </button>

      {open && (
        <div className="ob-panel">
          <div className="ob-panel-head">
            <strong>New orders</strong>
            {count > 0 && <button className="ob-link" onClick={markAllRead}>Mark all read</button>}
          </div>

          {orders.length === 0 ? (
            <div className="ob-empty">🔔 No new orders. You'll hear a chime when one comes in.</div>
          ) : (
            <ul className="ob-list">
              {orders.map((o) => (
                <li key={o._id} className="ob-item" onClick={() => openOrder(o)}>
                  {o.productId?.images?.[0]
                    ? <img src={o.productId.images[0]} alt="" />
                    : <span className="ob-noimg">🛍️</span>}
                  <div className="ob-item-info">
                    <div className="ob-item-top">
                      <span className="ob-item-name">{o.name}</span>
                      <span className="ob-item-price">₹{Number(o.totalPrice).toFixed(2)}</span>
                    </div>
                    <div className="ob-item-product">{o.productId?.name || "Product"}{o.quantity > 1 ? ` × ${o.quantity}` : ""}</div>
                    <div className="ob-item-meta">
                      {timeAgo(o.orderDate)}
                      {o.phoneNumber ? ` · 📞 ${o.phoneNumber}` : ""}
                    </div>
                  </div>
                </li>
              ))}
              {count > orders.length && <li className="ob-more">+{count - orders.length} more</li>}
            </ul>
          )}

          <div className="ob-panel-foot">
            {permission === "default" && (
              <button className="ob-link" onClick={enableDesktop}>🖥️ Turn on desktop alerts</button>
            )}
            {permission === "denied" && <span className="ob-hint">Desktop alerts blocked in browser settings</span>}
            <button className="ob-view-all" onClick={() => openOrder(null)}>View all orders →</button>
          </div>
        </div>
      )}

      {popup && (
        <div className="ob-popup" onClick={() => openOrder(popup.order)} role="status">
          <span className="ob-popup-icon">🛍️</span>
          <div>
            <div className="ob-popup-title">New order received</div>
            <div className="ob-popup-text">{popup.text}</div>
          </div>
          <button className="ob-popup-close" onClick={(e) => { e.stopPropagation(); setPopup(null); }}>×</button>
        </div>
      )}
    </div>
  );
};

export default OrderBell;
